import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Button, IconButton } from '@material-ui/core';
import i18n from 'i18n-js';
import { formatAmount } from 'utils';
import { Table } from 'components/UI';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import Can from 'components/Can';
import { Link } from 'react-router-dom';
import { InvoiceItemDetailsModal } from 'components/Modals';
import { useStyles } from './ReceivableItemsTable.styled';
import { useSelector } from 'react-redux';

const ReceivableItemsTable = ({
  receivable,
  loading,
  handleEditItem,
  handleCreateItem,
  handleDeleteItem,
  showItemDetailsLink,
}) => {
  const receivableItems = receivable?.invoice_items || [];

  const [currentReceivableItem, setCurrentReceivableItem] = useState(null);
  const [showReceivableItemDetailModal, setShowReceivableItemDetailModal] = useState(false);
  const classes = useStyles();
  const currentOrg = useSelector((state) => state.auth?.organizations?.current);
  const isEmitter = currentOrg?.id === receivable?.account_emitter?.holder_id;
  const allowNewItems = isEmitter;

  const handleCloseModal = () => {
    setCurrentReceivableItem(null);
    setShowReceivableItemDetailModal(false);
  };

  const columns = [
    {
      name: i18n.t('tableColumns.name'),
      selector: (row) => row?.invoice_item_type?.name,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.description'),
      cell: (row) => row?.description || row?.invoice_item_type?.description || '--',
      grow: 1,
    },
    {
      name: i18n.t('receivable.unit_price'),
      selector: (row) => '$' + formatAmount(row?.unit_price || 0),
      grow: 0,
    },
    {
      name: i18n.t('receivable.quantity'),
      selector: (row) =>
        showItemDetailsLink && row?.invoice_item_type?.pricing_type?.internal_items ? (
          <Link
            onClick={() => {
              setCurrentReceivableItem(row);
              setShowReceivableItemDetailModal(true);
            }}
            style={{ textTransform: 'capitalize', wordWrap: 'unset' }}
            data-cy={`item-details-${row.id}`}
            className={classes.link}
            to={'#'}
          >
            {i18n.t(
              `account_statement.${row?.invoice_item_type?.pricing_type?.internal_source_item}_ids`,
              {
                quantity: row?.quantity || 0,
              }
            )}
          </Link>
        ) : (
          <div style={{ whiteSpace: 'break-spaces' }}>
            {`${row?.q_humanized} (${row?.quantity} ${row?.invoice_item_type?.units?.total})` || 0}
          </div>
        ),
      grow: 1,
      style: {
        whiteSpace: 'break-spaces',
      },
    },
    {
      name: i18n.t('receivable.total'),
      selector: (row) => '$' + formatAmount(row?.line_pre_tax || 0),
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.actions'),
      selector: (row, index) =>
        showItemDetailsLink && row?.invoice_item_type?.pricing_type?.internal_items ? (
          i18n.t('common.n/a')
        ) : (
          <Box display="flex" alignItems="center">
            <Can I="update" on="invoice_item">
              <IconButton onClick={() => handleEditItem(row)} data-cy="update-item-btn">
                <EditIcon />
              </IconButton>
            </Can>
            <Can I="delete" on="invoice_item">
              <IconButton
                onClick={() => handleDeleteItem(row.id || index)}
                data-cy="delete-item-btn"
              >
                <DeleteIcon />
              </IconButton>
            </Can>
          </Box>
        ),
      grow: 0,
      omit: !isEmitter,
      minWidth: '9rem',
      center: true,
    },
  ];

  return (
    <Box data-cy="receivable-items-table">
      {showReceivableItemDetailModal ? (
        <InvoiceItemDetailsModal
          open={showReceivableItemDetailModal}
          invoiceItem={currentReceivableItem}
          handleCloseModal={handleCloseModal}
        />
      ) : null}
      <Box mb={2}>
        <Table data={receivableItems} columns={columns} loading={loading} pagination={false} />
      </Box>
      {allowNewItems ? (
        <Button
          color="primary"
          startIcon={<AddIcon />}
          data-cy="receivable-add-new-item-btn"
          style={{ textTransform: 'inherit' }}
          onClick={() => handleCreateItem(null)}
        >
          {i18n.t('receivable.add_new_item')}
        </Button>
      ) : (
        <div style={{ height: '2.5rem' }} />
      )}
    </Box>
  );
};

ReceivableItemsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  receivable: PropTypes.array.isRequired,
  handleEditItem: PropTypes.func.isRequired,
  handleDeleteItem: PropTypes.func.isRequired,
  handleCreateItem: PropTypes.func.isRequired,
};

ReceivableItemsTable.defaultProps = {
  receivable: [],
  loading: false,
  handleEditItem: () => {},
  handleDeleteItem: () => {},
  handleCreateItem: () => {},
};

export default ReceivableItemsTable;
